import { fetchApi } from './api';
import type {
    VatCompany, VatReturnResponse, VatEntriesResponse, VatSnapshotEntriesResponse, EcSalesResponse,
    IntrastatResponse, VatConfigResponse, VatPeriod, PeriodStatus,
    ConfigResource, ConfigRow, ConfigWriteResult, ConfigAuditResponse, ConfigImpactResponse,
    CarryForwardResponse, ExclusionDecisionResult, AnnualReturnResponse, StoredAnnualReturn,
    AmountOverrideField, AmountOverrideHistoryResponse, AmountOverrideResult,
} from '../types/vatReporting';

const BASE = '/vat';
const BASE_URL = '/api/v1';

export interface PeriodKey {
    company: VatCompany;
    country?: string;
    year: number;
    month: number;
}

function periodQuery(p: PeriodKey): URLSearchParams {
    const sp = new URLSearchParams();
    sp.set('company', p.company);
    sp.set('country', p.country ?? 'DE');
    sp.set('year', String(p.year));
    sp.set('month', String(p.month));
    return sp;
}

/**
 * Download a generated declaration file.
 *
 * Deliberately NOT using an <a download> with a data URL: the ZM file carries a byte-order
 * mark and the Intrastat file is Windows-1252, and letting the browser re-encode either of
 * them would hand the authority a file it reads as something else. The blob comes back
 * untouched from the server.
 */
async function downloadFile(url: string, fallbackName: string): Promise<void> {
    const res = await fetch(`${BASE_URL}${url}`);
    if (res.status === 401) {
        const { redirectToLogin } = await import('../utils/auth-redirect');
        redirectToLogin();
        throw new Error('Session expired');
    }
    if (!res.ok) {
        let message = `Download failed (status ${res.status})`;
        try {
            const body = await res.json() as { error?: { message?: string } };
            if (body.error?.message) message = body.error.message;
        } catch { /* non-JSON error page */ }
        throw new Error(message);
    }
    const disposition = res.headers.get('content-disposition') || '';
    const match = /filename="?([^"]+)"?/.exec(disposition);
    const filename = match?.[1] || fallbackName;
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
}

export const vatReportingApi = {
    getReturn(p: PeriodKey): Promise<VatReturnResponse> {
        return fetchApi(`${BASE}/returns?${periodQuery(p)}`);
    },

    getEntries(p: PeriodKey & {
        kennzahl?: string;
        vatBusPostingGroup?: string;
        vatProdPostingGroup?: string;
        direction?: string;
        entryNos?: number[];
        includeSettlement?: boolean;
        /** 'all' = the whole month, no Kennzahl/group filter - the Mehrwertsteuer-Posten tab. */
        scope?: 'all';
        /**
         * Show the manually excluded postings too, struck through, instead of dropping them.
         *
         * DELIBERATELY OFF FOR A KENNZAHL DRILLDOWN. That list has to be exactly what produced
         * the figure, or the footer sum and the figure disagree and one of them is lying. The
         * two callers that set it - the Posten tab and a control's evidence - are showing the
         * basis rather than a total.
         */
        includeExcluded?: boolean;
    }): Promise<VatEntriesResponse> {
        const sp = periodQuery(p);
        if (p.scope) sp.set('scope', p.scope);
        if (p.includeExcluded) sp.set('includeExcluded', 'true');
        if (p.kennzahl) sp.set('kennzahl', p.kennzahl);
        if (p.vatBusPostingGroup) sp.set('vatBusPostingGroup', p.vatBusPostingGroup);
        if (p.vatProdPostingGroup) sp.set('vatProdPostingGroup', p.vatProdPostingGroup);
        if (p.direction) sp.set('direction', p.direction);
        if (p.includeSettlement) sp.set('includeSettlement', 'true');
        if (p.entryNos?.length) sp.set('entryNos', p.entryNos.join(','));
        return fetchApi(`${BASE}/returns/entries?${sp}`);
    },

    /** The frozen transaction list of a filed period - `available: false` if there is none. */
    getSnapshotEntries(p: PeriodKey): Promise<VatSnapshotEntriesResponse> {
        return fetchApi(`${BASE}/returns/snapshot-entries?${periodQuery(p)}`);
    },

    getEcSales(p: PeriodKey): Promise<EcSalesResponse> {
        return fetchApi(`${BASE}/ec-sales?${periodQuery(p)}`);
    },

    downloadEcSales(p: PeriodKey): Promise<void> {
        return downloadFile(`${BASE}/ec-sales/export?${periodQuery(p)}`,
            `${p.year}-${String(p.month).padStart(2, '0')}-${p.company}-ZM.csv`);
    },

    getIntrastat(p: PeriodKey): Promise<IntrastatResponse> {
        return fetchApi(`${BASE}/intrastat?${periodQuery(p)}`);
    },

    downloadIntrastat(p: PeriodKey): Promise<void> {
        return downloadFile(`${BASE}/intrastat/export?${periodQuery(p)}`,
            `${p.year}-${p.month}-${p.company}_Intrastat.csv`);
    },

    /** The same lines as an xlsx with text cells - for reading only, never for the upload. */
    downloadIntrastatReview(p: PeriodKey): Promise<void> {
        return downloadFile(`${BASE}/intrastat/review?${periodQuery(p)}`,
            `${p.year}-${p.month}-${p.company}_Intrastat_Pruefliste.xlsx`);
    },

    getConfig(company: VatCompany, country = 'DE'): Promise<VatConfigResponse> {
        return fetchApi(`${BASE}/config?company=${company}&country=${country}`);
    },

    // --- the settings tab -------------------------------------------------------------
    //
    // THE KEY TRAVELS IN THE BODY, NOT IN THE PATH. Four of the five tables have a composite
    // natural key of up to five parts, and one part is legitimately the EMPTY STRING (dbi's
    // `EU / <empty> / Verkauf`). A path segment cannot carry that difference: an empty segment
    // and a missing one look identical. So create and update post the whole row, and only the
    // delete - which has no values to send - puts its key parts in the query string, where ''
    // and absent are still distinguishable.

    /** Create. The payload carries key fields AND values; the server refuses a duplicate key. */
    createConfigRow(resource: ConfigResource, row: ConfigRow): Promise<ConfigWriteResult> {
        return fetchApi(`${BASE}/config/${resource}`, {
            method: 'POST',
            body: JSON.stringify(row),
        });
    },

    /**
     * Full replace of one row's values. The key fields inside `row` identify it and are never
     * written - a key change is a delete plus a create, because these keys ARE the row.
     */
    updateConfigRow(resource: ConfigResource, row: ConfigRow): Promise<ConfigWriteResult> {
        return fetchApi(`${BASE}/config/${resource}`, {
            method: 'PUT',
            body: JSON.stringify(row),
        });
    },

    /** `key` must carry every key field, empty strings included. */
    deleteConfigRow(resource: ConfigResource, key: ConfigRow): Promise<ConfigWriteResult> {
        const sp = new URLSearchParams();
        for (const [k, v] of Object.entries(key)) sp.set(k, v === null ? '' : String(v));
        return fetchApi(`${BASE}/config/${resource}?${sp}`, { method: 'DELETE' });
    },

    getConfigAudit(p: {
        company: VatCompany; country?: string; resource?: ConfigResource; limit?: number;
    }): Promise<ConfigAuditResponse> {
        const sp = new URLSearchParams();
        sp.set('company', p.company);
        sp.set('country', p.country ?? 'DE');
        if (p.resource) sp.set('resource', p.resource);
        if (p.limit) sp.set('limit', String(p.limit));
        return fetchApi(`${BASE}/config/audit?${sp}`);
    },

    /** What a change to this key would move - both the open months and the filed ones. */
    getConfigImpact(p: {
        company: VatCompany; country?: string; resource: ConfigResource;
        vatBusPostingGroup?: string; vatProdPostingGroup?: string; direction?: string;
    }): Promise<ConfigImpactResponse> {
        const sp = new URLSearchParams();
        sp.set('company', p.company);
        sp.set('country', p.country ?? 'DE');
        sp.set('resource', p.resource);
        // Set only when defined: '' is a real product posting group, undefined means "any".
        if (p.vatBusPostingGroup !== undefined) sp.set('vatBusPostingGroup', p.vatBusPostingGroup);
        if (p.vatProdPostingGroup !== undefined) sp.set('vatProdPostingGroup', p.vatProdPostingGroup);
        if (p.direction !== undefined) sp.set('direction', p.direction);
        return fetchApi(`${BASE}/config/impact?${sp}`);
    },

    // --- manual exclusions and carry-forward (Phase 5) --------------------------------
    //
    // ONE POSTING, ONE MONTH, WITH A REASON. The server refuses any of these that would change
    // a filed month's figures (409, code PERIOD_FILED; `drop` changes none); the browser greys
    // the buttons out from `periodLocked` so the refusal is not the first thing a person meets,
    // but the server's refusal is the one that counts.

    /** Open deferrals from earlier months, this month's own decisions, and the trail. */
    getCarryForward(p: PeriodKey): Promise<CarryForwardResponse> {
        return fetchApi(`${BASE}/returns/carry-forward?${periodQuery(p)}`);
    },

    /** Take one posting out of the given month's declarations. The comment is mandatory. */
    excludeEntry(p: PeriodKey & { entryNo: number; comment: string }): Promise<ExclusionDecisionResult> {
        return fetchApi(`${BASE}/returns/entries/exclude`, {
            method: 'POST',
            body: JSON.stringify({ country: 'DE', ...p }),
        });
    },

    /** Carry a deferred posting into a period - `year`/`month` are the TARGET, not the origin. */
    includeEntry(p: PeriodKey & { entryNo: number; comment: string }): Promise<ExclusionDecisionResult> {
        return fetchApi(`${BASE}/returns/entries/include`, {
            method: 'POST',
            body: JSON.stringify({ country: 'DE', ...p }),
        });
    },

    /** Never declare it. No period: the decision already carries its origin month. */
    dropEntry(p: {
        company: VatCompany; country?: string; entryNo: number; comment: string;
    }): Promise<ExclusionDecisionResult> {
        return fetchApi(`${BASE}/returns/entries/drop`, {
            method: 'POST',
            body: JSON.stringify({ country: 'DE', ...p }),
        });
    },

    /**
     * Withdraw a decision entirely - the posting goes back into the month its own date puts it
     * in. The audit row stays: an undo is its own change, never an erasure.
     */
    resetEntryExclusion(p: {
        company: VatCompany; country?: string; entryNo: number;
    }): Promise<ExclusionDecisionResult> {
        return fetchApi(`${BASE}/returns/entries/reset`, {
            method: 'POST',
            body: JSON.stringify({ country: 'DE', ...p }),
        });
    },

    // --- manual EUR corrections (migration 229) --------------------------------------

    /** Set or change Basis EUR / MWST EUR of one posting. `year`/`month`: the month on screen. */
    setAmountOverride(p: PeriodKey & {
        entryNo: number; field: AmountOverrideField; amount: number; comment: string;
    }): Promise<AmountOverrideResult> {
        return fetchApi(`${BASE}/returns/entries/amount-override`, {
            method: 'POST',
            body: JSON.stringify({ country: 'DE', ...p }),
        });
    },

    /** Withdraw a correction; BC's figure applies again. The history keeps both. */
    resetAmountOverride(p: PeriodKey & {
        entryNo: number; field: AmountOverrideField; comment: string;
    }): Promise<AmountOverrideResult> {
        return fetchApi(`${BASE}/returns/entries/amount-override/reset`, {
            method: 'POST',
            body: JSON.stringify({ country: 'DE', ...p }),
        });
    },

    getAmountOverrideHistory(p: {
        company: VatCompany; country?: string; entryNo: number;
    }): Promise<AmountOverrideHistoryResponse> {
        const sp = new URLSearchParams();
        sp.set('company', p.company);
        sp.set('country', p.country ?? 'DE');
        sp.set('entryNo', String(p.entryNo));
        return fetchApi(`${BASE}/returns/entries/amount-override/history?${sp}`);
    },

    // --- the annual return (Jahresmeldung tab) ----------------------------------------

    /** The whole year in one call: matrix, annual form lines, checks, stored status. */
    getAnnual(p: { company: VatCompany; country?: string; year: number }): Promise<AnnualReturnResponse> {
        const sp = new URLSearchParams();
        sp.set('company', p.company);
        sp.set('country', p.country ?? 'DE');
        sp.set('year', String(p.year));
        return fetchApi(`${BASE}/annual?${sp}`);
    },

    /**
     * OMITTING A FIELD KEEPS WHAT IS STORED. The server rewrites the annual line values on
     * every status change, so `filedValues` is left out when reopening a year - otherwise the
     * figures transcribed off the transmitted protocol would be deleted by a button that only
     * says "reopen". Same rule for the ticket, the note and the protocol reference; sending an
     * empty string clears one on purpose.
     */
    setAnnualStatus(input: {
        company: VatCompany; country?: string; year: number;
        status: PeriodStatus;
        transferTicket?: string | null;
        note?: string | null;
        filedValues?: Record<string, number>;
    }): Promise<StoredAnnualReturn> {
        return fetchApi(`${BASE}/annual/status`, {
            method: 'POST',
            body: JSON.stringify({ country: 'DE', ...input }),
        });
    },

    setStatus(input: PeriodKey & {
        status: PeriodStatus;
        transferTicket?: string | null;
        ecSalesTransferTicket?: string | null;
        note?: string | null;
        filedValues?: Record<string, number>;
        acknowledgeIncompleteData?: boolean;
    }): Promise<VatPeriod> {
        return fetchApi(`${BASE}/returns/status`, {
            method: 'POST',
            body: JSON.stringify({ country: 'DE', ...input }),
        });
    },
};
